import { useState, useEffect } from 'react';
import {
    ComboBox,
    IComboBox,
    IComboBoxOption,
    IComboBoxStyles,
    SelectableOptionMenuItemType,
    ActionButton,
    Label,
    Dialog,
    DialogFooter,
    DialogType
} from "@fluentui/react";
import { FiHelpCircle } from 'react-icons/fi';
import { TextField } from '@fluentui/react/lib/TextField';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';

import styles from "./FolderPicker.module.css";

import { UserData } from "../../api";

interface Props {
    className?: string;
    allowFolderCreation?: boolean;
    onSelectedKeyChanged: (selectedFolders: string[]) => void;
    selectableFolders: IComboBoxOption[];
    selectedKeys: string[];
    userData: UserData;
}

export const FolderPicker = ({ className, allowFolderCreation, onSelectedKeyChanged, selectableFolders, selectedKeys, userData }: Props) => {
    const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false);
    const [newFolderText, setNewFolderText] = useState('');
    const [comboBoxOptions, setComboBoxOptions] = useState<IComboBoxOption[]>(selectableFolders);

    const comboBoxStyles: Partial<IComboBoxStyles> = { root: { maxWidth: 300 }, input: { cursor: 'pointer' } };
    const allowNewFolders = allowFolderCreation as boolean;
    const tooltipHtml = allowNewFolders ? "Select a folder to upload documents into" : "Select a folder to filter the search by";

    useEffect(() => {
        setComboBoxOptions(selectableFolders);
    }, [selectableFolders]);

    const onCreateFolder = () => {
        const newFolderValue = newFolderText.trim();
        if (!newFolderValue || newFolderValue === '') {
            alert('Please enter a folder name.');
        } else {
            const currentOptions = [...comboBoxOptions, { key: newFolderValue, text: newFolderValue }];
            setComboBoxOptions(currentOptions);
            onSelectedKeyChanged([newFolderValue]);
            setIsNewFolderDialogOpen(false);
        }
    };

    const onChange = (
        event: React.FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string,
    ): void => {
        const selected = option?.selected;
        const currentSelectedOptionKeys = selectedKeys.filter(key => key !== 'selectAll');
        const selectAllState = currentSelectedOptionKeys.length === comboBoxOptions.filter(
            option => (option.itemType === SelectableOptionMenuItemType.Normal || option.itemType === undefined)
        ).length;

        if (!allowNewFolders) {
            if (option) {
                if (option.itemType === SelectableOptionMenuItemType.SelectAll) {
                    if (selectAllState) {
                        onSelectedKeyChanged([]);
                    } else {
                        const updatedKeys = ['selectAll', ...comboBoxOptions.filter(
                            option => (option.itemType === SelectableOptionMenuItemType.Normal || option.itemType === undefined)
                        ).map(o => o.key as string)];
                        onSelectedKeyChanged(updatedKeys);
                    }
                } else {
                    const updatedKeys = selected
                        ? [...currentSelectedOptionKeys, option.key as string]
                        : currentSelectedOptionKeys.filter(k => k !== option.key);
                    if (updatedKeys.length === comboBoxOptions.filter(
                        option => (option.itemType === SelectableOptionMenuItemType.Normal || option.itemType === undefined)
                    ).length) {
                        updatedKeys.push('selectAll');
                    }
                    onSelectedKeyChanged(updatedKeys);
                }
            }
        } else {
            onSelectedKeyChanged([option!.key as string]);
        }
    };

    return (
        <div className={`${styles.folderArea} ${className ?? ""}`}>
            <div className={styles.folderSelection}>
                <Label>Folder Selection&nbsp;
                    <FiHelpCircle
                        data-tooltip-id="FolderSelection-tooltip"
                        data-tooltip-html={tooltipHtml}>
                    </FiHelpCircle>
                </Label>
                <ComboBox
                    id="folderPicker"
                    multiSelect={!allowNewFolders}
                    selectedKey={selectedKeys}
                    options={comboBoxOptions}
                    defaultSelectedKey={allowFolderCreation && !selectedKeys.length ? userData.userPrincipalName : undefined}
                    onChange={onChange}
                    styles={comboBoxStyles}
                    errorMessage={selectedKeys && selectedKeys.length == 0 ?
                        "Please select at least one folder"
                        : undefined}
                />
            </div>
            {allowNewFolders ? (
                <div className={styles.actionButton}>
                    <ActionButton
                        iconProps={{ iconName: 'NewFolder' }}
                        allowDisabledFocus
                        onClick={() => setIsNewFolderDialogOpen(true)}>
                        Create new folder
                    </ActionButton>
                    <Dialog
                        hidden={!isNewFolderDialogOpen}
                        onDismiss={() => setIsNewFolderDialogOpen(false)}
                        dialogContentProps={{
                            type: DialogType.largeHeader,
                            title: 'Create New Folder',
                        }}
                        modalProps={{
                            isBlocking: true,
                            styles: {
                                main: {
                                    maxWidth: '500px !important',
                                    minWidth: '400px !important',
                                },
                            },
                        }}>
                        <TextField value={newFolderText} onChange={(e, newValue) => setNewFolderText(newValue || '')} />
                        <DialogFooter>
                            <PrimaryButton
                                iconProps={{ iconName: 'NewFolder' }}
                                onClick={onCreateFolder}
                                text="Create" />
                            <DefaultButton
                                iconProps={{ iconName: 'Cancel' }}
                                onClick={() => setIsNewFolderDialogOpen(false)}
                                text="Cancel" />
                        </DialogFooter>
                    </Dialog>
                </div>) : ""}
        </div>
    );
};